/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * RecentScans — "All scans"
 * -------------------------
 * People scanned at the open events — or at the one event picked in the
 * scanner's event dropdown — most recent scan first, 50 at a time
 * ("Show more" loads the next 50). Checked in lists everyone who checked in,
 * including those who have since checked out; Checked out lists those who
 * left. The tab decides what a row leads with: its check-in time on Checked
 * in (with when they left, if they have), its check-out time on Checked
 * out (with when they came in). Search looks up a passcode on the server.
 *
 * A page is two reads: the passes, then their check-in and check-out times.
 *
 * The chip totals are COUNT(*)s on the events' own tables, never a full read:
 * Checked in is the desk's event_check_in total (all open events) or one count
 * for the picked event, Checked out is one count on event_check_out.
 *
 * It loads when shown and after that only on Refresh (which also refreshes
 * the desk counts) — scans saved on this desk are placed into it directly. On
 * a wide screen it sits beside the scanner; on a phone the scanner shows a
 * one-line summary and this opens full screen over it.
 */

import React, { useState } from 'react';
import { pageRows, useDebounced, useRecentScans } from '../../../hooks/useCheckInDesk';
import { clockIn } from './checkInRules';

const FILTERS = [
  { id: 'checked_in', label: 'Checked in' },
  { id: 'checked_out', label: 'Checked out' },
];

function RecentScans({ openIds, scope, eventsById, checkedIn, onClose, onRefresh }) {
  const [filter, setFilter] = useState('checked_in');
  const [search, setSearch] = useState('');
  const passcode = useDebounced(search.trim());
  const { list, checkedIn: scopedIn, out } = useRecentScans(openIds, { scope, filter, search: passcode }, true);
  const scoped = scope !== 'all' ? eventsById[scope] : null;

  const rows = pageRows(list.data);
  const countOf = { checked_in: scoped ? (scopedIn.data ?? null) : checkedIn, checked_out: out.data ?? null };
  const refreshing = list.isFetching && !list.isFetchingNextPage;

  return (
    <section className="ochk-recent" aria-label="All scans">
      <header className="ochk-recenthead">
        {onClose && (
          <button type="button" className="ochk-iconbtn" onClick={onClose} aria-label="Back to scanner">
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </button>
        )}
        <div>
          <h2>All scans</h2>
          <p>All scanners · {scoped ? scoped.title : 'events open now'}</p>
        </div>
        <button
          type="button"
          className="ochk-iconbtn"
          onClick={() => { list.refetch(); out.refetch(); if (scoped) scopedIn.refetch(); onRefresh?.(); }}
          aria-label="Refresh all scans"
          disabled={refreshing}
        >
          <i className={`bi bi-arrow-clockwise${refreshing ? ' ochk-spin' : ''}`} aria-hidden="true" />
        </button>
      </header>

      <label className="sr-only-org" htmlFor="ochk-recent-search">Find a passcode</label>
      <input
        id="ochk-recent-search"
        className="orgf-input ochk-search"
        type="search"
        placeholder="Search by passcode"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className="ochk-filters" role="group" aria-label="Filter all scans">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="ochk-filter"
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
          >
            {item.label}{countOf[item.id] != null && !passcode && <span>{countOf[item.id]}</span>}
          </button>
        ))}
      </div>

      {list.isError && (
        <div className="ochk-recentmsg">
          Couldn’t load the scans. Scanning still works.
          <button type="button" className="org-btn ghost" onClick={() => list.refetch()}>Retry</button>
        </div>
      )}
      {list.isLoading && <div className="ochk-recentmsg">Loading scans…</div>}
      {list.isSuccess && rows.length === 0 && (
        <div className="ochk-recentmsg">{passcode ? 'No scan matches that passcode.' : `No one has checked ${filter === 'checked_out' ? 'out' : 'in'} yet.`}</div>
      )}

      <ul className="ochk-recentlist">
        {rows.map((row) => {
          const event = eventsById[row.eventId];
          const time = (at) => clockIn(at, event?.timezone);
          const inTab = filter === 'checked_in';
          return (
            <li key={row.passId} className="ochk-recentrow">
              <span className={`ochk-avatar${inTab ? ' is-in' : ''}`} aria-hidden="true">
                <i className={`bi ${inTab ? 'bi-box-arrow-in-right' : 'bi-box-arrow-right'}`} />
              </span>
              <div className="ochk-recentmain">
                {row.name
                  ? <b className="ochk-name">{row.name}</b>
                  : <b className="ochk-code">{row.passcode || '—'}</b>}
                <span>
                  {row.name && row.passcode ? <span className="ochk-code">{row.passcode}</span> : null}
                  {row.name && row.passcode ? ' · ' : ''}
                  {event?.title || ''}
                </span>
              </div>
              {inTab ? (
                <div className="ochk-recentside">
                  <span className="ochk-tag is-ok">
                    {row.inAt ? <>In<time>{time(row.inAt)}</time></> : 'Checked in'}
                  </span>
                  {row.status === 'CHECKED_OUT' && row.outAt && (
                    <span className="ochk-sub"><em>Left</em>{time(row.outAt)}</span>
                  )}
                </div>
              ) : (
                <div className="ochk-recentside">
                  <span className="ochk-tag is-out">
                    Out<time>{time(row.outAt || row.changedAt)}</time>
                  </span>
                  {row.inAt && <span className="ochk-sub"><em>In</em>{time(row.inAt)}</span>}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {list.hasNextPage && (
        <button
          type="button"
          className="org-btn ghost ochk-more"
          onClick={() => list.fetchNextPage()}
          disabled={list.isFetchingNextPage}
        >
          {list.isFetchingNextPage ? 'Loading…' : `Show more · ${rows.length} of ${list.data.pages[0].count}`}
        </button>
      )}
    </section>
  );
}

export default RecentScans;
